import React from "react";
import { Link } from "react-router-dom";

/**
 * Shared button — Stitch variants, brand tokens only.
 *
 * Variants:
 *   primary   — Spectrum Blue fill, white text
 *   secondary — white fill, Spectrum Blue border/text
 *   accent    — Sunshine fill, ink text (large CTAs on blue)
 *   ghost     — translucent on Spectrum Blue (store badges, etc.)
 *   outlineOnLight — transparent fill, Spectrum Blue border/text (CTAs on light)
 *   link      — text-only ink → Spectrum Blue hover
 *
 * Sizes: sm | md | lg
 */

const base =
  "inline-flex items-center justify-center gap-2 font-headline rounded-brand transition-[background-color,border-color,color,box-shadow,transform,opacity] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue disabled:opacity-50 disabled:pointer-events-none";

const variants = {
  primary:
    "bg-spectrum-blue text-surface-white font-semibold shadow-brand-sm hover:opacity-95 active:scale-95",
  secondary:
    "bg-surface-white text-spectrum-blue font-semibold border-[1.5px] border-spectrum-blue hover:bg-cloud-mist",
  accent:
    "bg-sunshine text-ink font-bold shadow-brand-md hover:opacity-95 active:scale-95",
  ghost:
    "bg-white/10 text-surface-white font-semibold border border-white/20 hover:bg-white/20",
  /** White outline on Spectrum Blue (hero secondary) */
  outlineOnBlue:
    "bg-transparent border-2 border-surface-white text-surface-white font-semibold hover:bg-surface-white hover:text-spectrum-blue active:scale-95",
  outlineOnLight:
    "bg-transparent border-2 border-spectrum-blue text-spectrum-blue font-semibold hover:bg-spectrum-blue hover:text-surface-white active:scale-95",
  link: "bg-transparent text-ink font-semibold hover:text-spectrum-blue shadow-none px-0 py-0",
};

const sizes = {
  sm: "text-[14px] px-5 py-2.5",
  md: "text-[15px] px-7 py-[13px]",
  lg: "text-base px-8 py-4",
};

const Button = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  type = "button",
  href,
  to,
  as,
  ...rest
}) => {
  const sizeClass = variant === "link" ? "text-[14px]" : sizes[size] || sizes.md;
  const classes = `${base} ${variants[variant] || variants.primary} ${sizeClass} ${className}`.trim();

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }

  if (href || as === "a") {
    return (
      <a href={href} className={classes} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  );
};

export default Button;
